import { useEffect, useState } from "react";
import Icon from "../../../components/base/Icon";
import Indicator from "../../../components/base/Indicator";
import { toastError } from "../../../components/base/Toast";
import { useHub } from "../../../core/Global";
import SignupActions from "./SignupActions";
import "./SignupWallet.css";


interface SignupWalletProps {
    token?: string
    /** Already topped up — move on to the phone number. */
    onDone: () => void
    /** Leave the whole setup and open the console. The only other way off this step. */
    onSkipSetup: () => void
}

interface WalletInfo {
    hasSavedCard: boolean
    cardBrand?: string
    cardLast4?: string
    balance: number
    charged: number
    amounts: number[]
}

// Mirrors SignupWalletService.Amounts. Used until the step has heard back from the server, so
// the tiles are there to look at rather than appearing a moment later.
const DEFAULT_AMOUNTS = [50, 100, 150, 200, 250, 300];

const CARD_LOGOS: { [brand: string]: string } = {
    visa: "/assets/media/svg/card-logos/visa.svg",
    mastercard: "/assets/media/svg/card-logos/mastercard.svg",
    amex: "/assets/media/svg/card-logos/american-express-dark.svg",
    discover: "/assets/media/svg/card-logos/discover.png",
    diners: "/assets/media/svg/card-logos/diners-club.png",
    jcb: "/assets/media/svg/card-logos/jcb.png",
    unionpay: "/assets/media/svg/card-logos/unionpay.svg"
};


// Third step of setting the account up: the money it runs on.
//
// It sits in front of the two number steps rather than after them. A number is billed every
// month and the calls to it are paid for by the minute, both out of this wallet, so an account
// that has never put anything in is not given one — not even the number its plan includes.
// Skipping goes straight to the console, where the number can be taken once there is credit.
//
// Fixed amounts rather than a box to type in: this is the first thing a brand new account pays
// for, and a typed figure is one slip away from a charge nobody meant to make.
export default function SignupWallet(props: SignupWalletProps) {

    const hub = useHub();

    const [info, infoSet] = useState<WalletInfo>();
    const [loading, loadingSet] = useState<boolean>(true);
    const [amount, amountSet] = useState<number>();
    const [newCard, newCardSet] = useState<boolean>(false);
    const [working, workingSet] = useState<boolean>(false);

    useEffect(() => {
        hub.Post("/auth/signup/wallet/info", { token: props.token }, res => {
            if (res.success) infoSet(res.data);
            loadingSet(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const amounts = info?.amounts?.length ? info.amounts : DEFAULT_AMOUNTS;

    // The card is only offered when there is one to offer, so asking for a different one has to
    // go away with it — otherwise the step would claim a choice it does not have.
    const savedCard = (info?.hasSavedCard ?? false) && !newCard;

    const charge = () => {
        if (!amount) {
            toastError("Choose how much to add.");
            return;
        }

        workingSet(true);

        hub.Post("/auth/signup/wallet/charge", {
            token: props.token,
            amount: amount,
            newCard: newCard
        }, res => {
            if (!res.success) {
                workingSet(false);
                toastError(res.message ?? "Could not start the payment.");
                return;
            }

            // Already paid for in another tab — nothing to charge, just carry on.
            if (res.data?.done || !res.data?.url) {
                workingSet(false);
                props.onDone();
                return;
            }

            // Stripe's own page, or our confirm endpoint when the card on file is being charged
            // and the bank may ask for a 3-D Secure check. Either way the browser comes back to
            // the wizard with the step already moved on.
            document.location.href = res.data.url;
        });
    };

    if (loading) return <Indicator text="Please wait..." />;

    return (
        <div className="signup-wallet">

            <div className="wallet-amounts">
                {
                    amounts.map(a =>
                        <button type="button" key={a}
                            className={"wallet-amount" + (amount === a ? " selected" : "")}
                            onClick={() => amountSet(a)}>
                            <span className="wallet-amount-value">${a}</span>
                            {
                                amount === a &&
                                <Icon icon="circle-check" type="solid" size={14} className="wallet-amount-check" />
                            }
                        </button>
                    )
                }
            </div>

            <div className="wallet-pay">

                <div className="wallet-pay-head">
                    <Icon icon="credit-card" type="solid" size={14} className="text-gray-500 me-2" />
                    <span className="wallet-pay-title">Payment</span>
                </div>

                {
                    savedCard ?
                        <div className="wallet-card">
                            <img className="wallet-card-logo"
                                src={CARD_LOGOS[(info?.cardBrand ?? "").toLowerCase()] ?? "/assets/media/svg/card-logos/unknown.png"}
                                alt="" />
                            <div className="wallet-card-text">
                                <div className="wallet-card-name">
                                    {info?.cardBrand}&nbsp;••••&nbsp;{info?.cardLast4}
                                </div>
                                {/* Where it came from is not something this step knows. It is
                                    whatever card the account has on file -- the plan step saves
                                    one, but a Free plan never goes near a card, and an earlier
                                    top-up leaves one behind too. Naming the plan here told half
                                    the accounts that reach this step something untrue. */}
                                <div className="wallet-card-note">The card saved on your account.</div>
                            </div>
                            <button type="button" className="btn btn-sm btn-link wallet-card-switch"
                                onClick={() => newCardSet(true)} disabled={working}>
                                Use a different card
                            </button>
                        </div>
                        :
                        <div className="wallet-card wallet-card-new">
                            <Icon icon="lock" type="solid" size={16} className="text-gray-500 me-3" />
                            <div className="wallet-card-text">
                                <div className="wallet-card-name">A new card</div>
                                <div className="wallet-card-note">
                                    You{String.fromCharCode(39)}ll pay on our secure payment page, and we{String.fromCharCode(39)}ll
                                    keep the card for your next top-up.
                                </div>
                            </div>
                            {
                                (info?.hasSavedCard ?? false) &&
                                <button type="button" className="btn btn-sm btn-link wallet-card-switch"
                                    onClick={() => newCardSet(false)} disabled={working}>
                                    Use my saved card
                                </button>
                            }
                        </div>
                }

                {/* Said plainly here rather than discovered on the next step. */}
                <div className="wallet-gate">
                    <Icon icon="circle-info" type="solid" size={13} className="me-2" />
                    Your phone number comes next, and it needs credit in the wallet. You can skip this
                    and add credit later from the console — your number waits until you do.
                </div>

            </div>

            <SignupActions>
                <button type="button" className="btn btn-light" onClick={props.onSkipSetup} disabled={working}>
                    Skip &amp; go to console
                </button>

                <button type="button" className="btn btn-lg btn-primary min-w-200px ms-auto"
                    disabled={working || !amount} onClick={charge}>
                    {
                        working ?
                            <>
                                <span className="indicator-label me-2">Please Wait</span>
                                <Icon icon="spinner-third" spin />
                            </>
                            :
                            <>
                                <span className="indicator-label me-2">
                                    {amount ? `Add $${amount} & Continue` : "Continue"}
                                </span>
                                <Icon icon="chevron-right" />
                            </>
                    }
                </button>
            </SignupActions>

        </div>
    );
}
